import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Gpa = () => {
  const [grades, setGrades] = useState(['', '', '']); // Initializing with 3 courses
  const [credits, setCredits] = useState(['', '', '']); // Initializing with 3 courses
  const [gpa, setGpa] = useState(0);

  // Event handlers to update state values
  const handleGradeChange = (e, index) => {
    const newGrades = [...grades];
    newGrades[index] = parseFloat(e.target.value) || 0;
    setGrades(newGrades);
  };

  const handleCreditChange = (e, index) => {
    const newCredits = [...credits];
    newCredits[index] = parseFloat(e.target.value) || 0;
    setCredits(newCredits);
  };

  const addCourse = () => {
    setGrades([...grades, 0]);
    setCredits([...credits, 0]);
  };

  const removeCourse = (index) => {
    const newGrades = grades.filter((_, i) => i !== index);
    const newCredits = credits.filter((_, i) => i !== index);
    setGrades(newGrades);
    setCredits(newCredits);
  };

  useEffect(() => {
    const totalCredits = credits.reduce((sum, credit) => sum + credit, 0);
    if (totalCredits > 0) {
      const totalGradePoints = grades.reduce((sum, grade, index) => sum + (grade * credits[index]), 0);
      const calculatedGpa = totalGradePoints / totalCredits;
      setGpa(calculatedGpa);
    } else {
      setGpa(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [grades, credits]);


  return (
    <Layout title="GPA Calculator" path="gpa">
      <section className="section-padding" id="gpa-calculator">
        <div className="container mt-5">
          <div className="row">
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">GPA Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Course Inputs */}
                    {grades.map((_, index) => (
                      <div className="col-md-6 col-lg-4" key={index}>
                        <div className="mb-3">
                          <label
                            htmlFor={`grade-${index}`}
                            className="form-label"
                          >
                            Grade for Course {index + 1}
                            <span
                              className="ms-2 text-muted"
                              data-bs-toggle="popover"
                              data-bs-html="true"
                              data-bs-placement="top"
                              data-bs-content="<strong>Grade:</strong> Enter the grade for this course (e.g., 4.0 for A, 3.0 for B, etc.)."
                              style={{ cursor: "pointer" }}
                            >
                              <i className="bi bi-info-circle"></i>
                            </span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            id={`grade-${index}`}
                            placeholder="Enter grade"
                            value={grades[index]}
                            onChange={(e) => handleGradeChange(e, index)}
                          />
                        </div>
                        <div className="mb-3">
                          <label
                            htmlFor={`credit-${index}`}
                            className="form-label"
                          >
                            Credit Hours for Course {index + 1}
                            <span
                              className="ms-2 text-muted"
                              data-bs-toggle="popover"
                              data-bs-html="true"
                              data-bs-placement="top"
                              data-bs-content="<strong>Credit Hours:</strong> Enter the number of credit hours for this course."
                              style={{ cursor: "pointer" }}
                            >
                              <i className="bi bi-info-circle"></i>
                            </span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            id={`credit-${index}`}
                            placeholder="Enter credit hours"
                            value={credits[index]}
                            onChange={(e) => handleCreditChange(e, index)}
                          />
                        </div>
                        {/* Remove Course Button */}
                        <div className="col-12 text-end">
                          <button
                            className="btn btn-danger"
                            onClick={() => removeCourse(index)}
                          >
                            <i className="fa-solid fa-minus"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Add Course Button */}
                    <div className="col-12">
                      <button
                        style={{
                          backgroundColor: "#13547a",
                          color: "#fff",
                          border: "none",
                        }}
                        className="btn btn-primary"
                        onClick={addCourse}
                      >
                        <i className="fa-solid fa-plus"></i> 
                      </button>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        GPA:{" "}
                        <span className="fw-bold ms-3">{gpa.toFixed(2)}</span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>
      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the GPA Calculator
                </h3>

                {/* What is a GPA Calculator? */}
                <h4 className="mt-3 mb-3">What is a GPA Calculator?</h4>
                <p>
                  A **GPA Calculator** helps students calculate their Grade
                  Point Average by averaging their grades, taking into account
                  the respective course credits. This tool is crucial for
                  tracking academic progress and assessing performance in a more
                  structured manner.
                </p>

                {/* Why Use a GPA Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a GPA Calculator?</h4>
                <p>
                  This calculator helps students keep track of their academic
                  performance by calculating the average GPA across all courses.
                  It simplifies the process of monitoring your academic standing
                  and is useful for planning future semesters.
                </p>

                {/* Benefits of Using a GPA Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a GPA Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Track Academic Progress:</strong> Students can
                    easily monitor how well they are doing in their courses.
                  </li>
                  <li>
                    <strong>Standardized Assessment:</strong> Helps educational
                    institutions provide a consistent way of measuring student
                    performance.
                  </li>
                  <li>
                    <strong>Quick Results:</strong> Allows for quick assessment
                    of your academic standing, making it easier to plan for the
                    future.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a GPA Calculator?</h4>
                <p>The GPA calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Students:</strong> Helps students track and improve
                    their academic performance.
                  </li>
                  <li>
                    <strong>Educational Institutions:</strong> Schools and
                    universities can use it to standardize and measure academic
                    success.
                  </li>
                  <li>
                    <strong>Academic Advisors:</strong> Useful for helping
                    students plan their courses and improve their GPA.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How do I calculate GPA?</h4>
                <p>
                  GPA is calculated by multiplying the grade points for each
                  course by its respective credit hours, summing them up, and
                  then dividing that sum by the total number of credit hours.
                </p>

                <h4 className="mt-4 mb-3">
                  What’s the difference between GPA and CGPA?
                </h4>
                <p>
                  GPA refers to the average of grades earned in a single
                  semester, while CGPA (Cumulative Grade Point Average) is the
                  cumulative average across all semesters.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is GPA important for students?
                </h4>
                <p>
                  GPA serves as a measure of a student's academic achievements.
                  It plays a vital role in university admissions, scholarship
                  opportunities, and even in job applications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Gpa;
